'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { BrandForm, type BrandFormRef } from '@/components/partials/brands'
import { useCreateBrand } from '@/hooks'
import { CreateBrandParams } from '@/types/brand'
import { FormModeEnum } from '@/enums'

const CreateBrand = () => {
  const router = useRouter()
  const t = useTranslations()
  const queryClient = useQueryClient()
  const brandFormRef = useRef<BrandFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createBrand, isPending: isLoading } = useCreateBrand()

  const handleSubmit = (data: CreateBrandParams) => {
    createBrand(data, {
      onSuccess: async newBrand => {
        setIsRedirecting(true)
        queryClient.setQueryData(['brand', newBrand.id], newBrand)
        await queryClient.invalidateQueries({ queryKey: ['brands'] })
        toast.success(t('created_successfully'))
        router.push(`/products/brands/${newBrand.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateBrandParams) => {
    createBrand(data, {
      onSuccess: async newBrand => {
        queryClient.setQueryData(['brand', newBrand.id], newBrand)
        await queryClient.invalidateQueries({ queryKey: ['brands'] })
        const detailUrl = `/products/brands/${newBrand.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        brandFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (brandFormRef.current) {
        brandFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/products/brands')
  }

  return (
    <BrandForm
      key={formKey}
      ref={brandFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default CreateBrand
